import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FiEdit } from "react-icons/fi";
import { IoMdArrowBack } from "react-icons/io";
import { MdRefresh } from "react-icons/md";
import { getLabTestLookups } from "../../../../Service/labTests";
import Loader from "../../../../reusableUI/loader";
import {
  flattenLabTestLookups,
  LabTestDetails,
} from "./labTestShared";

const LabTestView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [record, setRecord] = useState(location.state?.labTest || null);
  const [loading, setLoading] = useState(!location.state?.labTest);
  const [error, setError] = useState("");

  const loadRecord = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getLabTestLookups();
      if (response?.error !== false) {
        throw new Error(response?.message || "Unable to load lab test.");
      }

      const found = flattenLabTestLookups(response).find(
        (test) => String(test?._id) === String(id),
      );
      if (!found) throw new Error("Lab test not found.");
      setRecord(found);
    } catch (loadError) {
      setError(
        loadError?.response?.data?.message ||
          loadError?.message ||
          "Unable to load lab test.",
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  if (loading && !record) {
    return (
      <div className="flex items-center justify-center">
        <Loader loading={loading} />
      </div>
    );
  }

  return (
    <div className="h-[100dvh] overflow-hidden bg-[#F5F7FB]">
      <div className="h-full overflow-y-auto overscroll-contain px-2 pb-8 sm:px-3">
        <div className="sticky top-0 z-30 -mx-2 border-b border-[#E4E8F1] bg-[#F5F7FB]/95 px-2 pt-2 backdrop-blur sm:-mx-3 sm:px-3">
          <div className="mx-auto mb-3 flex max-w-[1500px] items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <button
                type="button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#E0E4F0] bg-white text-[#374151] shadow-sm transition-colors hover:border-[#C8CEFF] hover:bg-[#F0F2FF]"
                aria-label="Back to lab tests"
                onClick={() => navigate("/admin/lab-tests")}
              >
                <IoMdArrowBack size={20} color="#111827" />
              </button>
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <h2 className="truncate text-[18px] font-poppins font-semibold text-[#1F2937] sm:text-[22px]">
                    {record?.parameterName || "Lab Test"}
                  </h2>
                  {loading && record && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#C8CEFF] border-t-[#4951EC]" aria-label="Refreshing" />
                  )}
                </div>
                {record && (
                  <p className="mt-0.5 truncate text-[11px] text-[#8A91A8] sm:text-[12px]">
                    {[record.department, record.testGroup].filter(Boolean).join(" / ") || "Lab test details"}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#E0E4F0] bg-white text-[#4951EC] shadow-sm transition-colors hover:border-[#C8CEFF] hover:bg-[#F0F2FF]"
                title="Refresh page"
                aria-label="Refresh lab test"
                onClick={loadRecord}
              >
                <MdRefresh size={18} className={loading ? "animate-spin" : ""} />
              </button>
            </div>

            {record && (
              <button
                type="button"
                className="flex h-9 shrink-0 items-center gap-1.5 rounded-xl bg-[#4951EC] px-3 text-[12px] font-poppins font-medium text-white shadow-[0_6px_14px_rgba(73,81,236,0.22)] transition-colors hover:bg-[#3942D4] sm:px-4"
                onClick={() => navigate(`/admin/lab-tests/${record._id}/edit`, { state: { labTest: record } })}
              >
                <FiEdit size={14} />
                Edit
              </button>
            )}
          </div>
        </div>

        {error && !record ? (
          <div className="mx-auto mt-4 flex max-w-[1500px] flex-col items-center rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
            <p className="text-sm text-red-600">{error}</p>
            <button
              type="button"
              className="mt-4 rounded-md bg-[#4951EC] px-4 py-2 text-sm text-white shadow-sm transition hover:bg-[#3942D4]"
              onClick={loadRecord}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="mx-auto mt-4 max-w-[1500px]">
            {error && (
              <p role="alert" className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs text-red-700">
                {error}
              </p>
            )}
            {record && <LabTestDetails record={record} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default LabTestView;
